/**
 * Device-test results summary card.
 *
 * Shows the most recent mic / headphone / network results for the
 * signed-in user, fetched from `users.last_device_test_results` via the
 * profile endpoint. Used in two places:
 *
 *   1. /account/devices — sits above the three test cards as a
 *      "what we last saw" snapshot, so a returning user can tell at a
 *      glance whether they need to re-run anything.
 *   2. The onboarding flow — the "is your studio ready?" gate renders
 *      this card to verify the user has tested their setup before the
 *      first record session.
 *
 * The component is read-only (no save logic of its own); the individual
 * test cards write their own section, then a profile-query invalidation
 * causes this card to re-render with the fresh value.
 */
import { copy } from '@ohear/shared'
import { Badge, Card } from '@ohear/ui'
import type { DeviceTestResultsSnapshot } from '../../lib/api/account'
import { useT } from '../../lib/i18n/use-t'

const STALE_AFTER_DAYS = 90

export interface DeviceTestResultsProps {
  /** Snapshot from `useAccountProfile().data?.last_device_test_results`. */
  results: DeviceTestResultsSnapshot | null | undefined
  /** Aggregate "most recent test" timestamp from the same profile row. */
  lastTestedAt: string | null | undefined
}

type Severity = 'pass' | 'warn' | 'fail'

function classifyMic(mic: NonNullable<DeviceTestResultsSnapshot['mic']>): Severity {
  if (mic.peakDbfs > -1) return 'fail'
  if (mic.noiseFloorDbfs > -45) return 'fail'
  if (mic.peakDbfs > -3 || mic.noiseFloorDbfs > -55) return 'warn'
  return 'pass'
}

function classifyNetwork(net: NonNullable<DeviceTestResultsSnapshot['network']>): Severity {
  if (net.uploadMbps < 2 || net.medianPingMs > 250) return 'fail'
  if (net.uploadMbps < 5 || net.medianPingMs > 100) return 'warn'
  return 'pass'
}

export function DeviceTestResults({ results, lastTestedAt }: DeviceTestResultsProps) {
  const hasAny = Boolean(results?.mic ?? results?.headphone ?? results?.network)
  const stale = isStale(lastTestedAt)

  return (
    <Card title="Your most recent test results">
      <div className="flex flex-col gap-3">
        {!hasAny ? (
          <p className="text-[13px] text-[var(--text-2)] m-0">
            No test results yet. Run the three checks below before your first session — they take
            less than two minutes total.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[13px] text-[var(--text-2)]">
              <span>Last updated:</span>
              <span className="text-[var(--text-1)] tabular-nums">
                {lastTestedAt ? formatRelative(lastTestedAt) : 'unknown'}
              </span>
              {stale ? <Badge qcSeverity="warn">Older than {STALE_AFTER_DAYS} days</Badge> : null}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[13px]">
              <Section
                label="Microphone"
                emoji="🎙️"
                tested={Boolean(results?.mic)}
                severity={results?.mic ? classifyMic(results.mic) : null}
                summary={
                  results?.mic
                    ? `Peak ${fmtDb(results.mic.peakDbfs)} • Noise ${fmtDb(results.mic.noiseFloorDbfs)}`
                    : null
                }
                testedAt={results?.mic?.testedAt}
              />
              <Section
                label="Headphones"
                emoji="🎧"
                tested={Boolean(results?.headphone)}
                severity={results?.headphone ? (results.headphone.passed ? 'pass' : 'warn') : null}
                summary={
                  results?.headphone
                    ? results.headphone.passed
                      ? 'Confirmed L / R / sweep'
                      : 'Reported an issue'
                    : null
                }
                testedAt={results?.headphone?.testedAt}
              />
              <Section
                label="Network"
                emoji="📶"
                tested={Boolean(results?.network)}
                severity={results?.network ? classifyNetwork(results.network) : null}
                summary={
                  results?.network
                    ? `${results.network.uploadMbps.toFixed(1)} Mbps up • ${Math.round(results.network.medianPingMs)} ms`
                    : null
                }
                testedAt={results?.network?.testedAt}
              />
            </dl>
          </>
        )}
      </div>
    </Card>
  )
}

function Section({
  label,
  emoji,
  tested,
  severity,
  summary,
  testedAt,
}: {
  label: string
  emoji: string
  tested: boolean
  severity: Severity | null
  summary: string | null
  testedAt: string | null | undefined
}) {
  const t = useT()
  return (
    <div className="flex flex-col gap-1 p-3 rounded-[var(--radius-sm)] bg-[var(--bg-2)]">
      <div className="flex items-center justify-between gap-2">
        <dt className="text-[12px] text-[var(--text-2)] m-0 flex items-center gap-1">
          <span aria-hidden="true">{emoji}</span> {label}
        </dt>
        {tested && severity ? (
          <Badge qcSeverity={severity}>
            {severity === 'pass' ? 'Pass' : severity === 'warn' ? 'OK' : 'Issue'}
          </Badge>
        ) : (
          <span className="text-[11px] text-[var(--text-2)]">
            {t(copy.common.EMPTY.not_yet_run)}
          </span>
        )}
      </div>
      <dd className="m-0 text-[12px] text-[var(--text-1)] tabular-nums">
        {summary ?? <span className="text-[var(--text-2)]">—</span>}
      </dd>
      {testedAt ? (
        <span className="text-[11px] text-[var(--text-2)]">{formatRelative(testedAt)}</span>
      ) : null}
    </div>
  )
}

function isStale(iso: string | null | undefined): boolean {
  if (!iso) return false
  const ms = Date.now() - new Date(iso).getTime()
  return ms > STALE_AFTER_DAYS * 24 * 60 * 60 * 1000
}

function formatRelative(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const deltaMs = Date.now() - d.getTime()
  const minutes = Math.round(deltaMs / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return d.toLocaleDateString()
}

function fmtDb(db: number): string {
  if (!Number.isFinite(db)) return '-inf dB'
  return `${db.toFixed(1)} dBFS`
}
